import { MOBILE_SCREEN_SIZE } from "@/components/AppNavigation/constants";
import { useCheckoutContext } from "@/components/Checkout/CheckoutMachine";
import { useSelector } from "@xstate/react";
import React, { useState } from "react";

import { Box, styled } from "@mui/material";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { cancelOrder } from "@/api/rest/checkoutApi";
import { useNavigate } from "react-router-dom";
import GenericDialog from "@/components/StoreComponents/StoreDialogs/GenericDialog";

export const ButtonsContainer = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 22,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: 10,
    gridTemplateColumns: "1fr",
  },
});

const SingleButtonContainer = styled("div")(({ theme }) => ({
  borderRadius: 7,
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: "#f6f8fa",
  border: `1px solid #dcdddc`,
  color: theme.vars.palette.text.primary,
  padding: "21px 15px 21px 25px",
  justifyContent: "space-between",
  cursor: "pointer",
  ".title": {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".subtitle": {
    fontSize: 14,
    fontWeight: 500,
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
}));

export const EditButton = ({
  label,
  subtitle,
  onClick,
}: {
  label: string;
  subtitle: string;
  onClick?: () => void;
}) => {
  return (
    <SingleButtonContainer onClick={onClick} role="button">
      <div className="link_info">
        <div className="title">{label}</div>
        <div className="subtitle">{subtitle}</div>
      </div>
      <KeyboardArrowRightRoundedIcon sx={{ fontSize: "24px" }} />
    </SingleButtonContainer>
  );
};

const EditOrderSection = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { actorRef, send } = useCheckoutContext();

  const isEditable = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.isEditable
  );

  const orderId = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.orderId
  );

  if (!isEditable) {
    return null;
  }

  const handleEditOrder = async () => {
    // call cancelOrder api with editItems true

    try {
      setLoading(true);
      const response = await cancelOrder({
        orderId: orderId ?? "",
        editItems: true,
      });
      if (response.status === "success") {
        navigate("/mybag");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      setLoading(true);
      const response = await cancelOrder({
        orderId: orderId ?? "",
        editItems: false,
      });

      if (response.status === "success" && response.userRetailerCheckoutOrder) {
        const userRetailerCheckoutOrder = response.userRetailerCheckoutOrder;
        send({
          type: "UPDATE_RETAILER_CHECKOUT_ORDER",
          data: {
            retailerCheckoutOrder: {
              ...userRetailerCheckoutOrder,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onCancelOrder = () => {
    GenericDialog.show({
      title: "This will cancel your order — are you sure?",
      text: "Just double-checking before it’s too late.",
      actionsProps: {
        sx: (theme) => ({
          "&.actions": {
            button: {
              fontSize: 14,
              height: 37,
            },
            [theme.breakpoints.down("md")]: {
              flexDirection: "column-reverse",
              button: {
                height: 44,
                fontSize: 14,
                maxWidth: 355,
              },
            },
          },
        }),
      },
      actions: [
        {
          text: "No, keep it",
          onClick: () => {},
          variant: "outline",
        },
        {
          text: "Yes, cancel my order",
          onClick: handleCancelOrder,
          variant: "solid",
        },
      ],
    });
  };

  return (
    <Box className="section">
      <div
        className="section_head"
        style={{ cursor: "pointer" }}
        onClick={() => {}}
      >
        <div className="title">{"Need to edit or cancel your order?"}</div>
      </div>
      <ButtonsContainer>
        <EditButton
          label={loading ? "Editing your order..." : "Edit your order"}
          subtitle="Make changes to the items in your bag"
          onClick={loading ? undefined : handleEditOrder}
        />
        <EditButton
          label={loading ? "Cancelling your order..." : "Cancel your order"}
          subtitle="Cancel this order and start over"
          onClick={loading ? undefined : onCancelOrder}
        />
      </ButtonsContainer>
    </Box>
  );
};

export default EditOrderSection;
